// services/healthService.js

// Time Frame IDs - dùng để filter data
export const TIME_FRAME_IDS = {
  DOI: 'b56c18ec-f4d6-4dbf-8b64-8916beeabbbf',      // Đói
  TRUOC_AN: 'a56c18ec-f4d6-4dbf-8b64-8916beeabbbf', // Trước ăn
  SAU_AN: 'a59c18ec-f4d6-4dbf-8b64-8916beeabbbf'    // Sau ăn
};

export const healthService = {
  // Glucose Trend
  async getGlucoseTrend(api, patientId, searchParams) {
    try {
      const { data } = await api.get('Admin/Glucose/Input', {
        params: { ...searchParams, patientId },
      });
      return data; // ✅ raw array
    } catch (error) {
      console.error('❌ Lỗi lấy Glucose:', error);
      throw error;
    }
  },

  // Format glucose trend to chart format with 3 separate series
  formatGlucoseTrendToChart(rawData) {
    try {
      if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
        return this.getDefaultGlucoseChart();
      }

      // Sắp xếp theo thời gian
      const sorted = [...rawData]
        .filter(x => x.glucose && x.createDate)
        .sort((a, b) => a.createDate - b.createDate);

      // Lấy unit từ data đầu tiên
      const unit = sorted[0]?.unit || 'mg/dL';

      // Group data theo timeFrameId
      const doiData = sorted.filter(x => x.timeFrameId === TIME_FRAME_IDS.DOI);
      const truocAnData = sorted.filter(x => x.timeFrameId === TIME_FRAME_IDS.TRUOC_AN);
      const sauAnData = sorted.filter(x => x.timeFrameId === TIME_FRAME_IDS.SAU_AN);

      return {
        unit,
        series: [
          {
            name: 'Đường huyết đói',
            data: doiData.map(item => ({
              x: new Date(item.createDate * 1000).getTime(),
              y: Number(item.glucose)
            })),
            color: '#3b82f6' // Blue
          },
          {
            name: 'Trước ăn',
            data: truocAnData.map(item => ({
              x: new Date(item.createDate * 1000).getTime(),
              y: Number(item.glucose)
            })),
            color: '#10b981' // Green
          },
          {
            name: 'Sau ăn',
            data: sauAnData.map(item => ({
              x: new Date(item.createDate * 1000).getTime(),
              y: Number(item.glucose)
            })),
            color: '#8b5cf6' // Purple
          }
        ]
      };
    } catch (err) {
      console.error('❌ Lỗi format Glucose:', err);
      return this.getDefaultGlucoseChart();
    }
  },

  getDefaultGlucoseChart() {
    return {
      labels: ['No data'],
      unit: 'mg/dL',
      series: [
        {
          name: 'Đường huyết đói',
          data: [0],
          color: '#3b82f6'
        },
        {
          name: 'Trước ăn',
          data: [0],
          color: '#10b981'
        },
        {
          name: 'Sau ăn',
          data: [0],
          color: '#8b5cf6'
        }
      ]
    };
  },

  // Lấy latest glucose value để hiển thị trong metric card
  getLatestGlucose(rawData) {
    if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
      return null;
    }

    // Sắp xếp theo thời gian và lấy giá trị mới nhất
    const sorted = [...rawData]
      .filter(x => x.glucose && x.createDate)
      .sort((a, b) => b.createDate - a.createDate);

    return sorted[0] ? {
      value: Number(sorted[0].glucose),
      unit: sorted[0].unit || 'mg/dL',
      type: sorted[0].type,
      timeFrame: sorted[0].timeFrame,
      color: sorted[0].backgroundColor,
      date: sorted[0].createDate
    } : null;
  },

  getDefaultChart() {
    return {
      labels: ['No data'],
      values: [0],
      color: '#d1d5db'
    };
  },

  //////////////////// HUYET AP //////////////////
  async getBloodPressureTrend(api, patientId, searchParams) {
    try {
      const { data } = await api.get('Admin/BloodPressure/Input', {
        params: {
          ...searchParams,
          patientId
        }
      });
      return data;
    } catch (error) {
      console.error('Lỗi lấy huyết áp:', error);
      throw error;
    }
  },

  formatBloodPressureTrendToChart(trendItems) {
    if (!trendItems || trendItems.length === 0) {
      return {
        series: [],
        colors: ['#ef4444', '#3b82f6', '#10b981'],
        yAxisMin: 60,
        yAxisMax: 180,
        unit: 'mmHg'
      };
    }

    const sortedItems = [...trendItems].sort((a, b) => a.date - b.date);

    // Lấy unit từ data đầu tiên
    const unit = sortedItems[0]?.unit || 'mmHg';

    // ✅ Format data với datetime (timestamp in milliseconds)
    const systolicData = sortedItems.map(item => ({
      x: new Date(item.date * 1000).getTime(),
      y: item.systolic || 120
    }));

    const diastolicData = sortedItems.map(item => ({
      x: new Date(item.date * 1000).getTime(),
      y: item.diastolic || 80
    }));

    // ✅ Thêm Pulse data
    const pulseData = sortedItems
      .filter(item => item.pulseRate && item.pulseRate > 0)
      .map(item => ({
        x: new Date(item.date * 1000).getTime(),
        y: item.pulseRate
      }));

    // Calculate y-axis range (include pulse values)
    const allValues = sortedItems.flatMap(item => [
      item.systolic,
      item.diastolic,
      item.pulseRate || 0
    ].filter(v => v > 0));
    const dataMin = Math.min(...allValues);
    const dataMax = Math.max(...allValues);
    const padding = (dataMax - dataMin) * 0.1;
    const yAxisMin = Math.floor(dataMin - padding);
    const yAxisMax = Math.ceil(dataMax + padding);

    return {
      series: [
        {
          name: 'Tâm thu',
          data: systolicData
        },
        {
          name: 'Tâm trương',
          data: diastolicData
        },
        {
          name: 'Nhịp tim',
          data: pulseData
        }
      ],
      colors: ['#ef4444', '#3b82f6', '#10b981'],
      yAxisMin,
      yAxisMax,
      unit
    };
  },

  // Lấy latest blood pressure để hiển thị trong metric card
  getLatestBloodPressure(rawData) {
    if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
      return null;
    }

    const sorted = [...rawData].sort((a, b) => b.date - a.date);

    return sorted[0] ? {
      systolic: sorted[0].systolic,
      diastolic: sorted[0].diastolic,
      unit: sorted[0].unit || 'mmHg',
      bloodPressureType: sorted[0].bloodPressureType,
      date: sorted[0].date
    } : null;
  },

  formatDateWithTime(timestamp) {
    try {
      const d = new Date(timestamp * 1000);
      if (isNaN(d.getTime())) return 'Invalid';
      return `${d.getDate()}/${d.getMonth() + 1}`;
    } catch (e) {
      return 'Unknown';
    }
  },

  //// HBA1C
  async getHba1cTrend(api, patientId, searchParams) {
    try {
      const { data } = await api.get('Admin/HbA1C/Input', {
        params: { ...searchParams, patientId }
      });
      return data;
    } catch (error) {
      console.error('❌ Lỗi lấy HbA1C:', error);
      throw error;
    }
  },

  formatHba1cTrendToChart(rawData) {
    if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
      return { labels: [], values: [], color: '#ef4444', unit: '%' };
    }

    const sorted = [...rawData].sort((a, b) => a.date - b.date);

    const labels = sorted.map(item => {
      const d = new Date(item.date * 1000);
      return `${d.getDate()}/${d.getMonth() + 1}`;
    });

    const values = sorted.map(item => item.hbA1C);

    // Lấy unit từ data
    const unit = sorted[0]?.unit || '%';

    const distinctColors = [...new Set(sorted.map(x => x.color))];
    const color = distinctColors.length === 1 ? distinctColors[0] : '#ef4444';

    return { labels, values, color, unit };
  },

  // Lấy latest HbA1C
  getLatestHbA1c(rawData) {
    if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
      return null;
    }

    const sorted = [...rawData].sort((a, b) => b.date - a.date);

    return sorted[0] ? {
      value: sorted[0].hbA1C,
      unit: sorted[0].unit || '%',
      type: sorted[0].type,
      date: sorted[0].date
    } : null;
  },

  //// BMI/WEIGHT 
  async getBmiTrend(api, patientId, searchParams) {
    try {
      const { data } = await api.get('Admin/Weight/Input', {
        params: { ...searchParams, patientId },
      });
      return data;
    } catch (error) {
      console.error('❌ Lỗi lấy BMI:', error);
      throw error;
    }
  },

  formatBmiTrendToChart(rawData) {
    if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
      return { 
        labels: [], 
        bmiValues: [], 
        weightValues: [], 
        color: '#21A567',
        weightUnit: 'kg',
        bmiUnit: ''
      };
    }

    const sorted = [...rawData].sort((a, b) => a.date - b.date);

    const labels = sorted.map((item) => {
      const d = new Date(item.date * 1000);
      return `${d.getDate()}/${d.getMonth() + 1}`;
    });

    const bmiValues = sorted.map((item) => item.bmi);
    const weightValues = sorted.map((item) => item.weight);
    const color = sorted[0]?.bmiColorCode || '#21A567';

    // Lấy units
    const weightUnit = sorted[0]?.weightUnit || 'kg';
    const bmiUnit = ''; // BMI không có unit

    return { labels, bmiValues, weightValues, color, weightUnit, bmiUnit };
  },

  // Lấy latest BMI và Weight
  getLatestBmiWeight(rawData) {
    if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
      return null;
    }

    const sorted = [...rawData].sort((a, b) => b.date - a.date);
    return sorted[0] ? {
      bmi: sorted[0].bmi,
      weight: sorted[0].weight,
      weightUnit: sorted[0].weightUnit || 'kg',
      bmiUnit: '',
      waist: sorted[0].waist,
      bmiType: sorted[0].bmiText,
       date: sorted[0].date
    } : null;
  },
   getLatestWaist(rawData) {
    if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
      return null;
    }

    // ✅ Filter chỉ những record có waist, sau đó sort và lấy cái gần nhất
    const recordsWithWaist = [...rawData]
      .filter(item => item.waist && item.waist > 0)
      .sort((a, b) => b.date - a.date);

    if (recordsWithWaist.length === 0) {
      return null;
    }

    return {
      waist: recordsWithWaist[0].waist,
      waistUnit: recordsWithWaist[0].waistUnit || 'cm',
      date: recordsWithWaist[0].date
    };
  },

  // ========== INDICATOR FUNCTIONS ==========

  // Tính sự thay đổi đường huyết giữa lần gần nhất và lần trước
  getGlucoseChange(rawData) {
    if (!rawData || !Array.isArray(rawData) || rawData.length < 2) {
      return { value: null, direction: null, percentage: null };
    }

    const sorted = [...rawData]
      .filter(x => x.glucose && x.createDate)
      .sort((a, b) => b.createDate - a.createDate);

    if (sorted.length < 2) {
      return { value: null, direction: null, percentage: null };
    }

    const latest = Number(sorted[0].glucose);
    const previous = Number(sorted[1].glucose);
    const diff = latest - previous;
    const percentage = previous !== 0 ? ((diff / previous) * 100).toFixed(1) : 0;

    return {
      value: Math.abs(diff).toFixed(1),
      direction: diff > 0 ? 'up' : diff < 0 ? 'down' : 'stable',
      percentage: Math.abs(percentage)
    };
  },

  // Tính sự thay đổi huyết áp
  getBloodPressureChange(rawData) {
    if (!rawData || !Array.isArray(rawData) || rawData.length < 2) {
      return {
        systolic: { value: null, direction: null },
        diastolic: { value: null, direction: null }
      };
    }

    const sorted = [...rawData].sort((a, b) => b.date - a.date);

    if (sorted.length < 2) {
      return {
        systolic: { value: null, direction: null },
        diastolic: { value: null, direction: null }
      };
    }

    const latestSys = sorted[0].systolic;
    const previousSys = sorted[1].systolic;
    const diffSys = latestSys - previousSys;

    const latestDia = sorted[0].diastolic;
    const previousDia = sorted[1].diastolic;
    const diffDia = latestDia - previousDia;

    return {
      systolic: {
        value: Math.abs(diffSys),
        direction: diffSys > 0 ? 'up' : diffSys < 0 ? 'down' : 'stable'
      },
      diastolic: {
        value: Math.abs(diffDia),
        direction: diffDia > 0 ? 'up' : diffDia < 0 ? 'down' : 'stable'
      }
    };
  },

  // Tính sự thay đổi cân nặng
  getWeightChange(rawData) {
    if (!rawData || !Array.isArray(rawData) || rawData.length < 2) {
      return { value: null, direction: null, percentage: null };
    }

    const sorted = [...rawData].sort((a, b) => b.date - a.date);

    if (sorted.length < 2) {
      return { value: null, direction: null, percentage: null };
    }

    const latest = sorted[0].weight;
    const previous = sorted[1].weight;
    const diff = latest - previous;
    const percentage = previous !== 0 ? ((diff / previous) * 100).toFixed(1) : 0;

    return {
      value: Math.abs(diff).toFixed(1),
      direction: diff > 0 ? 'up' : diff < 0 ? 'down' : 'stable',
      percentage: Math.abs(percentage)
    };
  },

  // Tính sự thay đổi HbA1C
  getHbA1cChange(rawData) {
    if (!rawData || !Array.isArray(rawData) || rawData.length < 2) {
      return { value: null, direction: null, percentage: null };
    }

    const sorted = [...rawData].sort((a, b) => b.date - a.date);

    if (sorted.length < 2) {
      return { value: null, direction: null, percentage: null };
    }

    const latest = sorted[0].hbA1C;
    const previous = sorted[1].hbA1C;
    const diff = latest - previous;
    const percentage = previous !== 0 ? ((diff / previous) * 100).toFixed(1) : 0;

    return {
      value: Math.abs(diff).toFixed(2),
      direction: diff > 0 ? 'up' : diff < 0 ? 'down' : 'stable',
      percentage: Math.abs(percentage)
    };
  },

  // Tính sự thay đổi BMI
  getBmiChange(rawData) {
    if (!rawData || !Array.isArray(rawData) || rawData.length < 2) {
      return { value: null, direction: null, percentage: null };
    }

    const sorted = [...rawData].sort((a, b) => b.date - a.date);

    if (sorted.length < 2) {
      return { value: null, direction: null, percentage: null };
    }

    const latest = sorted[0].bmi;
    const previous = sorted[1].bmi;
    const diff = latest - previous;
    const percentage = previous !== 0 ? ((diff / previous) * 100).toFixed(1) : 0;

    return {
      value: Math.abs(diff).toFixed(1),
      direction: diff > 0 ? 'up' : diff < 0 ? 'down' : 'stable',
      percentage: Math.abs(percentage)
    };
  },

  // Tính sự thay đổi vòng eo
  getWaistChange(rawData) {
    if (!rawData || !Array.isArray(rawData) || rawData.length < 2) {
      return { value: null, direction: null, percentage: null };
    }

    // Filter chỉ những record có waist
    const sorted = [...rawData]
      .filter(item => item.waist && item.waist > 0)
      .sort((a, b) => b.date - a.date);

    if (sorted.length < 2) {
      return { value: null, direction: null, percentage: null };
    }

    const latest = sorted[0].waist;
    const previous = sorted[1].waist;
    const diff = latest - previous;
    const percentage = previous !== 0 ? ((diff / previous) * 100).toFixed(1) : 0;

    return {
      value: Math.abs(diff).toFixed(1),
      direction: diff > 0 ? 'up' : diff < 0 ? 'down' : 'stable',
      percentage: Math.abs(percentage)
    };
  }
};

export const BLOOD_PRESSURE_TIMEFRAME = {
  ALL: 'all',
  WAKE_UP: '433e32dd-aa64-4292-b91d-c4686c0e2479',
  ANY_TIME: '700eaf84-271b-493b-8869-34823f94e800',
};